import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import localStorageWrapper from 'util/LocalStorageWrapper'
import DevToolsBadge from '../../components/DevToolsBadge'
import DevToolsPanel from '../../components/DevToolsPanel'

const STORAGE_KEY = 'db_dev_tools'
const WINDOW_KEY = '__dailybreakDevTools'

const defaultPersistedState = {
  customCssDisabled: false,
}

const isBrowser = () => typeof window !== 'undefined'

const readPersisted = () => {
  if (!isBrowser()) return defaultPersistedState
  try {
    const raw = localStorageWrapper.getItem(STORAGE_KEY)
    if (!raw) return defaultPersistedState
    const parsed = JSON.parse(raw)
    return { ...defaultPersistedState, ...parsed }
  } catch {
    return defaultPersistedState
  }
}

const writePersisted = state => {
  if (!isBrowser()) return
  try {
    localStorageWrapper.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore
  }
}

export const devToolsContext = createContext({
  customCssDisabled: false,
  toggleCustomCssDisabled: () => {},
  setCustomCssDisabled: () => {},
  panelOpen: false,
  openPanel: () => {},
  closePanel: () => {},
  togglePanel: () => {},
})

export const useDevTools = () => useContext(devToolsContext)

export const DevToolsProvider = ({ children }) => {
  const [persisted, setPersisted] = useState(defaultPersistedState)
  const [panelOpen, setPanelOpen] = useState(false)

  useEffect(() => {
    setPersisted(readPersisted())
  }, [])

  const setCustomCssDisabled = useCallback(value => {
    setPersisted(prev => {
      const next = { ...prev, customCssDisabled: Boolean(value) }
      writePersisted(next)
      return next
    })
  }, [])

  const toggleCustomCssDisabled = useCallback(() => {
    setPersisted(prev => {
      const next = { ...prev, customCssDisabled: !prev.customCssDisabled }
      writePersisted(next)
      return next
    })
  }, [])

  const openPanel = useCallback(() => setPanelOpen(true), [])
  const closePanel = useCallback(() => setPanelOpen(false), [])
  const togglePanel = useCallback(() => setPanelOpen(prev => !prev), [])

  useEffect(() => {
    if (!isBrowser()) return
    window[WINDOW_KEY] = {
      status: () => ({ customCssDisabled: persisted.customCssDisabled }),
      toggleCustomCss: () => toggleCustomCssDisabled(),
      disableCustomCss: () => setCustomCssDisabled(true),
      enableCustomCss: () => setCustomCssDisabled(false),
      openPanel: () => openPanel(),
      closePanel: () => closePanel(),
      help: () => {
        // eslint-disable-next-line no-console
        console.log(
          [
            'Dailybreak Dev Tools',
            '  .status()             current toggle state',
            '  .toggleCustomCss()    flip customCss on/off',
            '  .disableCustomCss()   force off',
            '  .enableCustomCss()    force on',
            '  .openPanel()          open floating panel',
            '  .closePanel()         close floating panel',
          ].join('\n'),
        )
      },
    }
    return () => {
      if (window[WINDOW_KEY]) delete window[WINDOW_KEY]
    }
  }, [
    persisted.customCssDisabled,
    toggleCustomCssDisabled,
    setCustomCssDisabled,
    openPanel,
    closePanel,
  ])

  const value = useMemo(
    () => ({
      customCssDisabled: persisted.customCssDisabled,
      toggleCustomCssDisabled,
      setCustomCssDisabled,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
    }),
    [
      persisted.customCssDisabled,
      toggleCustomCssDisabled,
      setCustomCssDisabled,
      panelOpen,
      openPanel,
      closePanel,
      togglePanel,
    ],
  )

  return (
    <devToolsContext.Provider value={value}>
      {children}
      <DevToolsBadge />
      <DevToolsPanel />
    </devToolsContext.Provider>
  )
}
